import ChapterCoverContainer from "containers/ChapterCoverContainer";
import classnames from "classnames";
import ControlsContainer from "containers/controls/SingleVideoControlsContainer";
import CSSTransitionGroup from "react-addons-css-transition-group";
import getBlockPlayerWaitingOnExternalLibrary from "selectors/loading/getBlockPlayerWaitingOnExternalLibrary";
import getPlaybackState from "selectors/chapter/getPlaybackState";
import getShowControls from "selectors/getShowControls";
import getWaitingForDashJs from "selectors/device/getWaitingForDashJs";
import LoadingCoverComponent from "components/LoadingCoverComponent";
import NavigationWrapper from "components/NavigationWrapper";
import PropTypes from "prop-types";
import React, { Component } from "react";
import VideoContainer from "containers/VideoContainer";
import { AnalyticsActions } from "utils/Analytics";
import { changePlaybackState } from "actions/PlaybackStateActions";
import { compose, onlyUpdateForPropTypes, withHandlers } from "recompose";
import { connect } from "react-redux";
import { reportActivity } from "actions/InactivityActions";
import styles from "./VideoPlayer.css";

class VideoPlayer extends Component {
  static propTypes = {
    autoplay: PropTypes.bool.isRequired,
    blockPlayer: PropTypes.bool.isRequired,
    captionLanguage: PropTypes.string,
    chapterId: PropTypes.number.isRequired,
    forceChapterCover: PropTypes.bool.isRequired,
    navigationActive: PropTypes.bool.isRequired,
    onEnded: PropTypes.func.isRequired,
    onInitialPlay: PropTypes.func.isRequired,
    onPlay: PropTypes.func.isRequired,
    onReportActivity: PropTypes.func.isRequired,
    onScrub: PropTypes.func.isRequired,
    scrubbing: PropTypes.bool.isRequired,
    showChapterCover: PropTypes.bool.isRequired,
    showClosedCaptions: PropTypes.bool,
    videoId: PropTypes.number.isRequired,
    viewportHeight: PropTypes.number,
    viewportWidth: PropTypes.number,
  };

  componentDidMount() {
    if (this.props.autoplay) {
      this.props.onPlay();
    }
    if (!this.props.showChapterCover) {
      this.props.onInitialPlay();
    }
  }

  componentDidUpdate(oldProps) {
    const noLongerShowingChapterCover =
      oldProps.showChapterCover && !this.props.showChapterCover;
    if (noLongerShowingChapterCover) {
      this.props.onInitialPlay();
    }

    if (oldProps.scrubbing && !this.props.scrubbing) {
      this.props.onScrub();
    }
  }

  get allowVideoToBeActive() {
    return !this.props.showChapterCover;
  }

  render() {
    if (this.props.blockPlayer) {
      return <LoadingCoverComponent />;
    }

    const chapterCover = (
      <ChapterCoverContainer chapterId={this.props.chapterId} />
    );

    const stateKey = `chapter-${this.props.chapterId}`;

    const navigation = this.props.navigationActive ? (
      <NavigationWrapper chapterId={this.props.chapterId} />
    ) : null;

    const videoRootClassNames = classnames(
      "verse-chapter",
      styles.videoPlayerRoot,
      {
        [styles.controlsActive]: this.props.navigationActive,
      },
    );

    const style = {
      height: this.props.viewportHeight,
      width: this.props.viewportWidth,
    };

    return (
      <div className="verse" style={style}>
        <div
          className="player-container"
          onMouseMove={this.props.onReportActivity}
          onTouchStart={this.props.onReportActivity}
        >
          <div className={videoRootClassNames}>
            <CSSTransitionGroup
              transitionEnterTimeout={1000}
              transitionLeaveTimeout={1000}
              transitionName={{
                appear: styles.navTransitionAppear,
                appearActive: styles.navTransitionAppearActive,
                enter: styles.navTransitionEnter,
                enterActive: styles.navTransitionEnterActive,
                leave: styles.navTransitionLeave,
                leaveActive: styles.navTransitionLeaveActive,
              }}
            >
              {navigation}
            </CSSTransitionGroup>
            <VideoContainer
              active={this.allowVideoToBeActive}
              allowDurationChange={false}
              autoplay
              chapterId={this.props.chapterId}
              onEnded={this.props.onEnded}
              onUserInteractionInitiatedPlayback={() => {}} // Ignore proptype warning since we have chapter cover
              playAd={false}
              playingSubchapter={false}
              stateKey={stateKey}
              useFocalPoint
              videoId={this.props.videoId}
            />
            <CSSTransitionGroup
              className="chapter-cover-container"
              transitionEnterTimeout={1400}
              transitionLeaveTimeout={1400}
              transitionName="verse-cover"
            >
              {this.props.showChapterCover ? chapterCover : null}
            </CSSTransitionGroup>

            <ControlsContainer
              active={this.allowVideoToBeActive}
              chapterId={this.props.chapterId}
            />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const forceChapterCover = state.forceChapterCover;
  const videoId = parseInt(Object.keys(state.story.entities.videos)[0], 10);
  const playbackState = getPlaybackState(state, ownProps);
  return {
    autoplay: true,
    blockPlayer:
      getBlockPlayerWaitingOnExternalLibrary(state) ||
      getWaitingForDashJs(state),
    duration: state.story.entities.videos[videoId].duration,
    forceChapterCover,
    navigationActive: getShowControls(state, ownProps),
    playbackState,
    scrubbing: playbackState.scrubbing,
    showChapterCover: forceChapterCover,
    videoId,
    viewportHeight: state.viewport.dimensions.height,
    viewportWidth: state.viewport.dimensions.width,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  onReportActivity: () => {
    dispatch(reportActivity());
  },
  onPlay: () => {
    dispatch(
      changePlaybackState(ownProps, {
        playing: true,
      }),
    );
  },
  onInitialPlay: () => {
    AnalyticsActions.singleVideoViewed();
    AnalyticsActions.mediaProgress(ownProps, null, 0); // initial value
  },
});

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps,
  onEnded() {
    AnalyticsActions.mediaProgress(
      {
        ...ownProps,
        ...stateProps,
      },
      null,
      stateProps.duration,
    );
  },
  onInitialPlay() {
    AnalyticsActions.singleVideoViewed();
    AnalyticsActions.mediaProgress(
      {
        ...ownProps,
        ...stateProps,
      },
      null,
      0,
    );
  },
  onScrub() {
    AnalyticsActions.scrub(stateProps);
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps, mergeProps);

export default compose(
  connector,
  withHandlers({
    onEnded: (props) => props.onEnded,
    onInitialPlay: (props) => props.onInitialPlay,
    onPlay: (props) => props.onPlay,
    onReportActivity: (props) => props.onReportActivity,
    onScrub: (props) => props.onScrub,
  }),
  onlyUpdateForPropTypes,
)(VideoPlayer);
